import React, { useState, useRef, useEffect } from "react";
import { TiArrowBack } from "react-icons/ti";
import {
  useDownloadPurlFileMutation,
  useGetPurlDocumentsQuery,
  useLazyGetPurlFolderByKeyQuery,
} from "../../services/api";
import PurlDocumentCard from "../MyDocumentTile/PurlDocumentCard";
import styles from "./index.module.css";
import { tracker } from "../../services/mixpanel";
import { useAppSelector } from "../../store/store";
import {
  DocumentTabledata,
  DocumentTable2data,
  TrancheTabledata,
} from "../../utils/data";
import { showNotification } from "../../services/showNotification";
import { HiDocumentSearch, HiOutlineDownload } from "react-icons/hi";
import { config } from "../../configs";
const previewFiles = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "pdf",
  "txt",
  "html",
  "mp4",
  "webm",
  "ogg",
  "mp3",
  "wav",
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/apng",
  "image/avif",
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
  "audio/webm",
  "audio/aac",
  "video/mp4",
  "video/webm",
  "video/ogg",
  "text/plain",
  "text/html",
  "text/css",
  "text/javascript",
  "application/javascript",
  "application/json",
  "application/xml",
  "text/xml",
];

const MISSING_FORM_TOKEN = config.REACT_APP_MISSING_FORM_TOKEN || "";

const PurlDocumentTile = ({
  initialFolder,
  props,
  onClose,
}: {
  initialFolder: Folder;
  props?: any;
  onClose?: () => void;
}) => {
  const { zohoCustomerId, email } = useAppSelector((state) => state.login);
  const [navigating, setNavigating] = useState(false);

  const [currentFolder, setCurrentFolder] = useState<Folder>(initialFolder);
  const [folderStack, setFolderStack] = useState<Folder[]>([initialFolder]);
  const [trancheId, setTrancheId] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [contactSectionId, setContactSectionId] = useState<string | null>(null);
  const [folderName, setFolderName] = useState<string | null>(null);
  const [sectionName, setSectionName] = useState<string | null>(null);
  const showSidebar = props?.showSidebar == undefined;
  const isDocument = props.type == "Reports";
  const showDocumentKey = initialFolder.showDocumentKey ?? false;

  const {
    data: documentsData,
    isLoading,
    isFetching,
  } = useGetPurlDocumentsQuery(
    {
      key: currentFolder?.key?.replace(/\s+/g, "_") ?? "",
      trancheContactId: zohoCustomerId,
      trancheId,
      assetId,
      contactSectionId,
      folderName: folderName,
      sectionName: sectionName,
    },
    {
      skip: !currentFolder.key || navigating,
    }
  );
  const [downloadFile] = useDownloadPurlFileMutation();
  const [triggerGetFolder] = useLazyGetPurlFolderByKeyQuery();
  const loading = isLoading || isFetching;

  // Transform pre-fetched child folders into the same shape as documents
  const preloadedChildItems = (currentFolder.children || []).map((child) => ({
    _id: child._id,
    name: child.name,
    key: child.key,
    nextKey: child.nextKey || child.key,
    s3Key: null,
    isFolder: true,
    fileUrl: null,
  }));

  // Transform actual documents from API
  console.log(documentsData?.data);
  const apiDocumentItems =
    documentsData?.data?.map((item: any) => ({
      _id: item._id,
      name: item.name ?? item._id,
      nextKey: item.nextKey ?? null,
      s3Key: item.s3Key || null,
      isFolder: item.isFolder,
      fileUrl: item.fileUrl || item.webViewLink || null,
      key: item.key,
      mimeType: item.mimeType,
    })) || [];

  // Final merged list: child folders FIRST (instant), then files (async)
  const items = [...preloadedChildItems, ...apiDocumentItems];

  const handleFolderClick = (item: any) => {
    tracker.dashboard.documentModal.onOpen(item.name);

    if (currentFolder.key === "Tranches Names") setTrancheId(item._id);
    if (currentFolder.key === "Assets Deal Names") setAssetId(item._id);
    if (currentFolder.key === "Contact Sections") setContactSectionId(item._id);
    if (/^\d{4}$/.test(item.name)) {
      setFolderName(item.name);
    }

    // If inside Asset Files, ignore year
    if (currentFolder.key === "Asset Files") {
      setSectionName(item.name); // sectionName
    }

    const nextFolder: Folder = {
      _id: item._id,
      name: item.name,
      key: item.nextKey || item.key,
      parentId: currentFolder._id,
      children: [],
    };

    setFolderStack((prev) => [...prev, nextFolder]);
    setCurrentFolder(nextFolder);
  };

  const goBack = () => {
    if (folderStack.length <= 1) {
      return;
    }
    setNavigating(true);
    const newStack = [...folderStack];
    newStack.pop();
    setFolderStack(newStack);
    setCurrentFolder(newStack[newStack.length - 1]);
    setNavigating(false);
  };

  const handleFileView = async (fileId: string) => {
    const file = items.find((i) => i._id === fileId);
    if (!file) return;

    const key = file.s3Key || "";
    const response = (await downloadFile({ key })) as any;
    const downloadUrl = response?.data?.data?.url;
    if (!downloadUrl) return;

    const ext = downloadUrl.split(".").pop()?.toLowerCase() || "xlsx";

    if (
      (ext && previewFiles.includes(ext)) ||
      previewFiles.includes(response?.data?.data?.type || "")
    ) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    showNotification(
      "info",
      "This file can’t be opened here. We’ll download it instead."
    );

    try {
      // Fetch blob → gives full control over filename
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();

      // Build clean name (you already know how to do this part)
      const urlObj = new URL(downloadUrl);
      let rawName = urlObj.pathname.split("/").pop() || `document.${ext}`;

      let cleanName = rawName
        .replace(/%2520/g, "%20")
        .replace(/%20/g, " ")
        .replace(/\+/g, " ")
        .trim()
        .replace(/\s+/g, " ");

      // Safety: ensure extension
      if (!cleanName.includes(".")) {
        cleanName += `.${ext}`;
      }

      console.log("Clean name for download:", cleanName);

      // Force download with correct name
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.name || cleanName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (err) {
      console.error(err);
      showNotification("error", "Could not download file");
    }
  };

  const handleBackOrClose = () => {
    if (folderStack.length > 1) {
      goBack();
    } else {
      onClose?.();
    }
  };

  const handleFileDownload = async (file: any) => {
    const files = items.find((i) => i._id === file._id) || "";
    const response = await fetch(files.fileUrl);
    const blob = await response.blob();

    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = files.name;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };



  const openMissingDocForm = () => {
    const encodedEmail = encodeURIComponent(email ?? "");

    window.open(
      `https://forms.zohopublic.com/inceptionfinancial956/form/Missingfilesform/formperma/${MISSING_FORM_TOKEN}?Email=${encodedEmail}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className={styles.content}>
      {/* Back Button */}

      <span
        onClick={handleBackOrClose}
        className="w-[100px] flex bg-slate-100 rounded py-1 mb-1 border border-slate-300 cursor-pointer select-none hover:bg-slate-200 transition"
      >
        <TiArrowBack className="text-2xl px-2 w-[40px]" />
        Back
      </span>

      <div className="w-full">
        <h2 className="mb-5 text-2xl">
          {initialFolder.subFolderTitle || initialFolder.name}
        </h2>

        <div className="flex justify-between">
          {/* Main Document List */}
          <div className="bg-white w-full  max-h-[85vh] overflow-y-auto p-5 mr-4 min-h-[40vh]">
            <div className="flex justify-between items-center border-b pb-3 mb-4 px-3">
              <div className="font-medium flex-1">
                Name
              </div>
              <div className="flex items-center gap-2">
                <div className="font-medium text-center w-[120px]">
                  Action
                </div>
              </div>
            </div>

            <PurlDocumentCard
              items={items}
              onFolderClick={handleFolderClick}
              onFileView={handleFileView}
              onFileDownload={handleFileDownload}
              loading={loading && preloadedChildItems.length === 0} // Only show loader if no folders visible
            />

            <div className="h-10" />
          </div>

          {showSidebar && showDocumentKey && (
            <div className="flex flex-col">
              <div className="bg-white p-2 h-fit">
                <div className="flex items-center justify-center p-3">
                  DOCUMENT KEY
                </div>
                <div>
                  <table className={styles.customtable}>
                    <thead>
                      {isDocument && (
                        <tr>
                          <th className="text-sm p-2">General</th>
                          <th className="text-sm p-2">Definition</th>
                        </tr>
                      )}
                    </thead>
                    {isDocument ? (
                      <tbody className="h-fit ">
                        {DocumentTabledata.map((item: any, index: any) => (
                          <tr key={index}>
                            <td>{item.heading}</td>
                            <td>{item.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    ) : (
                      <tbody>
                        {TrancheTabledata.map((item: any, index: any) => (
                          <tr key={index}>
                            <td>{item.heading}</td>
                            <td>{item.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
              {isDocument && (
                <div className="bg-white mt-4">
                  <div className="p-2">
                    <table className={styles.customtable}>
                      <thead>
                        {isDocument ? (
                          <tr>
                            <th className="text-sm p-2">Bookkeeping</th>
                            <th className="text-sm p-2">Definition</th>
                          </tr>
                        ) : (
                          <></>
                        )}
                      </thead>

                      <tbody>
                        {DocumentTable2data.map((item: any, index: any) => (
                          <tr key={index}>
                            <td>{item.heading}</td>
                            <td>{item.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {showSidebar && (
          <button
            className={styles.missingDocBtn}
            onClick={() => openMissingDocForm()}
          >
            <HiDocumentSearch size={20} />I am missing a document
          </button>
        )}
      </div>
    </div>
  );
};

export default PurlDocumentTile;
